import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { HeroUserPillComponent } from './hero-user-pill.component';
import { PortalNavbarComponent } from './portal-navbar.component';
import { AuthService } from '../services/auth.service';
import { ArtifactAssetDto, GamePreferencesService } from '../services/game-preferences.service';

type ManagerSection = 'sounds' | 'video' | 'themes';
type ManagerCategoryId = 'right-answer' | 'wrong-answer' | 'end-of-round' | 'next-team-turn' | 'next-round' | 'intro' | 'break';

interface ManagerCategory {
  id: ManagerCategoryId;
  label: string;
  description: string;
}

@Component({
  selector: 'app-game-events-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, PortalNavbarComponent, HeroUserPillComponent],
  template: `
    <div class="page">
      <app-portal-navbar></app-portal-navbar>

      <section class="page-hero">
        <div class="page-hero-inner">
          <a routerLink="/" class="home-btn">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
            Home
          </a>
          <div class="hero-top-row">
            <div class="hero-icon hero-icon--violet">
              <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="4" width="18" height="16" rx="2"/>
                <path d="M7 8h10"/>
                <path d="M7 12h6"/>
                <path d="M7 16h4"/>
              </svg>
            </div>
            <app-hero-user-pill></app-hero-user-pill>
          </div>
          <h1 class="page-title">Game Events</h1>
          <p class="page-sub">Manage global sound and video artifacts for game event playback.</p>
        </div>
      </section>

      <section class="page-body">
        <div class="page-body-inner">
          @if (!isAuthorized()) {
            <div class="access-card">
              <h2 class="access-title">Site Admin only</h2>
              <p class="access-text">Only SiteAdmin can view Game Events.</p>
            </div>
          } @else {
            @if (message()) {
              <div class="status-banner">{{ message() }}</div>
            }

            <div class="pref-nav">
              <button class="pref-btn" [class.pref-btn--active]="activeSection() === 'sounds'" (click)="activeSection.set('sounds')">Sounds</button>
              <button class="pref-btn" [class.pref-btn--active]="activeSection() === 'video'" (click)="activeSection.set('video')">Video</button>
              <button class="pref-btn" [class.pref-btn--active]="activeSection() === 'themes'" (click)="activeSection.set('themes')">Themes</button>
            </div>

            <div class="pref-panel">
              @if (activeSection() === 'sounds' || activeSection() === 'video') {
                <div class="pref-panel-hd">
                  <span class="pref-panel-title">{{ activeSection() === 'sounds' ? 'Sounds' : 'Video' }}</span>
                  <span class="pref-panel-sub">Create, replace, and delete reusable event artifacts.</span>
                </div>
                <div class="pref-panel-body">
                  <div class="sounds-layout">
                    <div class="sound-cat-list">
                      @for (cat of activeCategories; track cat.id) {
                        <div class="sound-cat-card"
                             [class.sound-cat-card--active]="selectedCategory() === cat.id"
                             (click)="selectCategory(cat.id)">
                          <div class="sound-cat-info">
                            <span class="sound-cat-label">{{ cat.label }}</span>
                            <span class="sound-cat-desc">{{ cat.description }}</span>
                          </div>
                        </div>
                      }
                    </div>

                    <div class="sound-picker">
                      <p class="sound-picker-hd">
                        {{ activeSection() === 'sounds' ? 'Choose one sound' : 'Choose one video' }} for <strong>{{ activeCategory.label }}</strong>
                      </p>

                      <div class="asset-upload-row">
                        <input #uploadInput class="asset-upload-input" type="file" [accept]="activeSection() === 'sounds' ? 'audio/*' : 'video/*,.webm,.webp,image/webp'" (change)="onUploadFileSelected($event)">
                        <button type="button" class="asset-upload-btn" (click)="uploadNewArtifact(uploadInput)">Upload</button>
                        <button type="button" class="asset-upload-btn" [disabled]="!selectedArtifact()" (click)="triggerReplacePicker()">Replace</button>
                        <button type="button" class="asset-upload-btn asset-upload-btn--danger" [disabled]="!selectedArtifact()" (click)="deleteSelectedArtifact()">Delete</button>
                        <input #replaceInput class="sr-only" type="file" [accept]="activeSection() === 'sounds' ? 'audio/*' : 'video/*,.webm,.webp,image/webp'" (change)="onReplaceFileSelected($event)">
                      </div>

                      <div class="artifact-grid">
                        @for (artifact of visibleArtifacts(); track artifact.id) {
                          <div class="artifact-tile">
                            <button class="artifact-card"
                                    type="button"
                                    [class.artifact-card--active]="selectedArtifact()?.id === artifact.id"
                                    (click)="selectArtifact(artifact)">
                              <div class="artifact-card-icon">
                                @if (activeSection() === 'sounds') {
                                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                    <path d="M9 18V5l12-2v13"/>
                                    <circle cx="6" cy="18" r="3"/>
                                    <circle cx="18" cy="16" r="3"/>
                                  </svg>
                                } @else {
                                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                    <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"/>
                                    <line x1="7" y1="2" x2="7" y2="22"/>
                                    <line x1="17" y1="2" x2="17" y2="22"/>
                                    <line x1="2" y1="12" x2="22" y2="12"/>
                                  </svg>
                                }
                              </div>
                              <span class="artifact-card-name">{{ artifact.fileName }}</span>
                              <span class="artifact-card-meta">{{ formatSize(artifact.sizeBytes) }}</span>
                              @if (artifact.default) {
                                <span class="artifact-card-default">Default</span>
                              }
                            </button>
                            <button class="asset-toggle-btn"
                                    type="button"
                                    (click)="toggleArtifactPreview(artifact, $event)">
                              {{ isArtifactPlaying(artifact.id) ? 'Hide' : 'Play' }}
                            </button>
                            @if (isArtifactPlaying(artifact.id) && (previewUrl(artifact.id) || previewErrorMessage())) {
                              <div class="artifact-preview">
                                @if (previewUrl(artifact.id)) {
                                  @if (artifact.artifactType.toLowerCase() === 'sound') {
                                    <audio controls [src]="previewUrl(artifact.id)" (loadeddata)="clearMediaDecodeError()" (error)="onMediaDecodeError($event, artifact)"></audio>
                                  } @else {
                                    <video controls class="preview-video" [src]="previewUrl(artifact.id)" (loadeddata)="clearMediaDecodeError()" (error)="onMediaDecodeError($event, artifact)"></video>
                                  }
                                }
                                @if (previewErrorMessage()) {
                                  <div class="preview-error">{{ previewErrorMessage() }}</div>
                                }
                              </div>
                            }
                          </div>
                        } @empty {
                          <div class="empty-state">No artifacts found for this media type.</div>
                        }
                      </div>
                    </div>
                  </div>
                </div>
              } @else {
                <div class="pref-panel-hd">
                  <span class="pref-panel-title">Themes</span>
                  <span class="pref-panel-sub">Reserved for future management.</span>
                </div>
                <div class="pref-panel-body">
                  <div class="empty-state">This section will be enabled in a later phase.</div>
                </div>
              }
            </div>
          }
        </div>
      </section>
    </div>
  `,
  styles: [`
    .page { display: flex; flex-direction: column; min-height: 100vh; }
    .page-hero { background: radial-gradient(ellipse at 12% 60%, color-mix(in srgb, #8b5cf6 8%, transparent) 0%, transparent 55%), linear-gradient(155deg, var(--color-bg-start) 0%, var(--color-bg-end) 100%); border-bottom: 1px solid var(--color-border); padding: 3rem 1.5rem 3.5rem; }
    .page-hero-inner { display: flex; flex-direction: column; gap: 1rem; margin: 0 auto; max-width: 1280px; }
    .home-btn { align-items: center; align-self: flex-start; background: color-mix(in srgb, var(--color-surface-2) 65%, transparent); border: 1px solid var(--color-border); border-radius: 0.5rem; color: var(--color-muted); display: flex; font-size: 0.8rem; font-weight: 500; gap: 0.35rem; padding: 0.32rem 0.7rem; text-decoration: none; }
    .hero-top-row { align-items: center; display: flex; flex-wrap: wrap; gap: 0.85rem; }
    .hero-icon { align-items: center; border-radius: 0.75rem; display: flex; height: 52px; justify-content: center; width: 52px; }
    .hero-icon--violet { background: color-mix(in srgb, #8b5cf6 16%, transparent); color: #a78bfa; }
    .page-title { color: var(--color-text); font-size: clamp(1.6rem, 3vw, 2.2rem); font-weight: 800; line-height: 1.15; margin: 0; }
    .page-sub { color: var(--color-muted); font-size: 0.93rem; line-height: 1.6; margin: 0; max-width: 52ch; }
    .page-body { flex: 1; padding: 2.5rem 1.5rem; }
    .page-body-inner { display: flex; flex-direction: column; gap: 1.25rem; margin: 0 auto; max-width: 1280px; }
    .access-card, .pref-panel { background: color-mix(in srgb, var(--color-surface-2) 75%, transparent); border: 1px solid var(--color-border); border-radius: 1rem; }
    .access-card { padding: 1.5rem; }
    .access-title { color: var(--color-text); margin: 0 0 0.5rem; }
    .access-text { color: var(--color-muted); margin: 0; }
    .pref-nav { display: flex; flex-wrap: wrap; gap: 0.75rem; }
    .pref-btn { background: color-mix(in srgb, var(--color-surface-2) 75%, transparent); border: 1px solid var(--color-border); border-radius: 0.6rem; color: var(--color-muted); cursor: pointer; font-size: 0.85rem; font-weight: 600; padding: 0.6rem 1.15rem; }
    .pref-btn--active { background: color-mix(in srgb, #8b5cf6 14%, transparent); border-color: color-mix(in srgb, #8b5cf6 50%, var(--color-border)); color: #a78bfa; }
    .pref-panel-hd { align-items: baseline; border-bottom: 1px solid var(--color-border); display: flex; gap: 0.75rem; padding: 0.85rem 1.25rem; }
    .pref-panel-title { color: var(--color-text); font-size: 0.88rem; font-weight: 700; }
    .pref-panel-sub { color: var(--color-muted); font-size: 0.75rem; }
    .pref-panel-body { padding: 1.25rem; }
    .sounds-layout { display: grid; gap: 1.25rem; grid-template-columns: 240px 1fr; }
    .sound-cat-list { display: flex; flex-direction: column; gap: 0.5rem; }
    .sound-cat-card { background: color-mix(in srgb, var(--color-surface-2) 60%, transparent); border: 1px solid var(--color-border); border-radius: 0.65rem; cursor: pointer; display: flex; gap: 0.65rem; padding: 0.75rem 0.9rem; }
    .sound-cat-card--active { background: color-mix(in srgb, #8b5cf6 13%, transparent); border-color: color-mix(in srgb, #8b5cf6 50%, var(--color-border)); border-left: 3px solid #8b5cf6; padding-left: calc(0.9rem - 2px); }
    .sound-cat-info { display: flex; flex: 1; flex-direction: column; gap: 0.15rem; min-width: 0; }
    .sound-cat-label { color: var(--color-text); font-size: 0.83rem; font-weight: 600; }
    .sound-cat-desc { color: var(--color-muted); font-size: 0.7rem; line-height: 1.4; }
    .sound-picker { display: flex; flex-direction: column; gap: 1rem; }
    .sound-picker-hd { color: var(--color-muted); margin: 0; }
    .asset-upload-row { align-items: center; display: flex; flex-wrap: wrap; gap: 0.5rem; }
    .conversion-banner { border: 1px solid color-mix(in srgb, #f59e0b 40%, transparent); background: color-mix(in srgb, #f59e0b 12%, transparent); border-radius: 0.55rem; color: #fde68a; font-size: 0.75rem; padding: 0.45rem 0.6rem; }
    .asset-upload-input { color: var(--color-muted); font-size: 0.72rem; }
    .asset-upload-btn { background: color-mix(in srgb, #8b5cf6 22%, transparent); border: 1px solid color-mix(in srgb, #8b5cf6 48%, var(--color-border)); border-radius: 0.45rem; color: #c4b5fd; cursor: pointer; font-size: 0.72rem; font-weight: 600; padding: 0.3rem 0.65rem; }
    .asset-upload-btn:disabled { cursor: not-allowed; opacity: 0.5; }
    .asset-upload-btn--danger { background: color-mix(in srgb, #ef4444 16%, transparent); border-color: color-mix(in srgb, #ef4444 35%, var(--color-border)); color: #fca5a5; }
    .artifact-grid { display: grid; gap: 0.75rem; grid-template-columns: repeat(3, minmax(0, 1fr)); }
    .artifact-tile { display: flex; flex-direction: column; gap: 0.35rem; }
    .artifact-card { align-items: center; background: color-mix(in srgb, var(--color-surface-2) 60%, transparent); border: 1px solid var(--color-border); border-radius: 0.85rem; color: inherit; cursor: pointer; display: flex; flex-direction: column; gap: 0.5rem; padding: 1rem; text-align: center; }
    .artifact-card--active { background: color-mix(in srgb, #8b5cf6 13%, transparent); border-color: color-mix(in srgb, #8b5cf6 55%, var(--color-border)); }
    .artifact-card-icon { color: #a78bfa; }
    .artifact-card-name { color: var(--color-text); font-size: 0.82rem; font-weight: 600; word-break: break-word; }
    .artifact-card-meta { color: var(--color-muted); font-size: 0.7rem; }
    .artifact-card-status { border: 1px solid color-mix(in srgb, #22c55e 40%, transparent); background: color-mix(in srgb, #22c55e 12%, transparent); border-radius: 9999px; color: #86efac; font-size: 0.62rem; font-weight: 700; line-height: 1; padding: 0.15rem 0.45rem; }
    .artifact-card-status--pending { border-color: color-mix(in srgb, #f59e0b 40%, transparent); background: color-mix(in srgb, #f59e0b 14%, transparent); color: #fcd34d; }
    .artifact-card-default { background: color-mix(in srgb, #8b5cf6 16%, transparent); border-radius: 9999px; color: #c4b5fd; font-size: 0.62rem; font-weight: 700; padding: 0.14rem 0.55rem; }
    .asset-toggle-btn { background: color-mix(in srgb, var(--color-surface-2) 70%, transparent); border: 1px solid var(--color-border); border-radius: 0.4rem; color: var(--color-muted); cursor: pointer; font-size: 0.66rem; font-weight: 600; margin-top: 0.2rem; padding: 0.2rem 0.45rem; }
    .artifact-preview { display: flex; flex-direction: column; gap: 0.45rem; }
    .artifact-preview audio, .preview-video { width: 100%; border-radius: .65rem; border: 1px solid var(--color-border); background: #000; }
    .preview-error { border: 1px solid color-mix(in srgb, #ef4444 45%, transparent); background: color-mix(in srgb, #ef4444 10%, transparent); color: #fecaca; border-radius: .5rem; padding: .45rem .6rem; font-size: .78rem; }
    .empty-state { color: var(--color-muted); font-size: 0.83rem; padding: 2rem; text-align: center; }
    .sr-only { display: none; }
    @media (max-width: 900px) { .artifact-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
    @media (max-width: 700px) { .sounds-layout { grid-template-columns: 1fr; } .artifact-grid { grid-template-columns: 1fr; } }
  `]
})
export class GameEventsPageComponent implements OnInit, OnDestroy {
  private static readonly PendingStatuses = new Set(['pendingconversion', 'processingconversion']);

  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly gamePreferencesService = inject(GamePreferencesService);

  private readonly maxSoundBytes = 5 * 1024 * 1024;
  private readonly maxVideoBytes = 5 * 1024 * 1024;
  private readonly allowedSoundExtensions = new Set(['.m4a', '.wav']);
  private readonly allowedVideoExtensions = new Set(['.mp4', '.wmv', '.mov', '.webm', '.webp']);

  readonly isAuthorized = signal(false);
  readonly message = signal('');
  readonly activeSection = signal<ManagerSection>('sounds');
  readonly selectedSoundCategory = signal<ManagerCategoryId>('right-answer');
  readonly selectedVideoCategory = signal<ManagerCategoryId>('right-answer');
  readonly artifacts = signal<ArtifactAssetDto[]>([]);
  readonly selectedArtifact = signal<ArtifactAssetDto | null>(null);
  readonly activePreviewAssetId = signal<number | null>(null);
  readonly previewErrorMessage = signal('');

  private currentOrganizationId: number | null = null;
  private pendingUploadFile: File | null = null;
  private readonly previewUrlMap = new Map<number, string>();
  private conversionPollingHandle: ReturnType<typeof setInterval> | null = null;

  readonly soundCategories: ManagerCategory[] = [
    { id: 'right-answer', label: 'Right Answer', description: 'Manage right-answer sounds.' },
    { id: 'wrong-answer', label: 'Wrong Answer', description: 'Manage wrong-answer sounds.' },
    { id: 'end-of-round', label: 'End of Round', description: 'Manage end-of-round sounds.' },
    { id: 'next-team-turn', label: 'Next Team Turn', description: 'Manage next-turn sounds.' },
    { id: 'next-round', label: 'Next Round', description: 'Manage next-round sounds.' },
    { id: 'intro', label: 'Intro', description: 'Manage intro sounds.' },
    { id: 'break', label: 'Break', description: 'Manage break sounds.' }
  ];

  readonly videoCategories: ManagerCategory[] = [
    { id: 'right-answer', label: 'Right Answer', description: 'Manage right-answer videos.' },
    { id: 'wrong-answer', label: 'Wrong Answer', description: 'Manage wrong-answer videos.' },
    { id: 'end-of-round', label: 'End of Round', description: 'Manage end-of-round videos.' },
    { id: 'next-team-turn', label: 'Next Team Turn', description: 'Manage next-turn videos.' },
    { id: 'next-round', label: 'Next Round', description: 'Manage next-round videos.' },
    { id: 'intro', label: 'Intro', description: 'Manage intro videos.' },
    { id: 'break', label: 'Break', description: 'Manage break videos.' }
  ];

  get activeCategories(): ManagerCategory[] {
    return this.activeSection() === 'sounds' ? this.soundCategories : this.videoCategories;
  }

  get selectedCategory() {
    return this.activeSection() === 'sounds' ? this.selectedSoundCategory : this.selectedVideoCategory;
  }

  get activeCategory(): ManagerCategory {
    const categories = this.activeCategories;
    return categories.find(c => c.id === this.selectedCategory()) ?? categories[0];
  }

  visibleArtifacts(): ArtifactAssetDto[] {
    const artifactType = this.activeSection() === 'sounds' ? 'sound' : 'video';
    const category = this.activeCategory.id;
    return this.artifacts()
      .filter(a => a.artifactScope?.toLowerCase() === 'base')
      .filter(a => a.artifactType.toLowerCase() === artifactType && a.artifactCategory === category)
      .sort((a, b) => a.fileName.localeCompare(b.fileName));
  }

  async ngOnInit(): Promise<void> {
    const authorized = await this.authService.hasRoleAsync('SiteAdmin');
    this.isAuthorized.set(authorized);

    if (!authorized) {
      void this.router.navigate(['/']);
      return;
    }

    const organizationId = await this.authService.getOrganizationIdAsync();
    if (organizationId && organizationId > 0) {
      this.currentOrganizationId = organizationId;
      this.loadArtifacts();
      return;
    }

    this.currentOrganizationId = null;
    this.artifacts.set([]);
    this.selectedArtifact.set(null);
    this.message.set('Unable to determine your organization. Upload is unavailable.');
  }

  ngOnDestroy(): void {
    for (const url of this.previewUrlMap.values()) {
      URL.revokeObjectURL(url);
    }
    this.previewUrlMap.clear();
    this.stopConversionPolling();
  }

  selectArtifact(artifact: ArtifactAssetDto): void {
    if (this.isConversionPending(artifact)) {
      this.message.set('Video conversion is still in progress. Set default after conversion completes.');
      return;
    }

    this.message.set('');
    this.gamePreferencesService.setArtifactDefault(artifact.id).subscribe({
      next: (updated) => {
        this.selectedArtifact.set(updated);
        this.loadArtifacts();
      },
      error: (err) => console.error('Failed to set artifact default:', err)
    });
  }

  selectCategory(categoryId: ManagerCategoryId): void {
    if (this.activeSection() === 'sounds') {
      this.selectedSoundCategory.set(categoryId);
    } else {
      this.selectedVideoCategory.set(categoryId);
    }
    this.selectedArtifact.set(null);
  }

  onUploadFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.pendingUploadFile = input.files?.[0] ?? null;

    if (!this.pendingUploadFile) {
      this.message.set('');
      return;
    }

    this.message.set('');
  }

  uploadNewArtifact(uploadInput?: HTMLInputElement): void {
    if (!this.pendingUploadFile) {
      this.message.set('Select a file first.');
      uploadInput?.click();
      return;
    }

    if (!this.validateFile(this.pendingUploadFile)) return;

    const organizationId = this.currentOrganizationId;
    if (!organizationId || organizationId <= 0) {
      this.message.set('Unable to determine your organization. Upload is unavailable.');
      return;
    }

    this.gamePreferencesService.uploadArtifact({
      organizationId,
      artifactType: this.activeSection() === 'sounds' ? 'Sound' : 'Video',
      artifactCategory: this.activeCategory.id,
      artifactScope: 'Base',
      preferenceSection: this.activeSection(),
      preferenceCategory: this.activeCategory.id
    }, this.pendingUploadFile).subscribe({
      next: (artifact) => {
        this.artifacts.update(list => [...list, artifact]);
        this.selectedArtifact.set(artifact);
        this.pendingUploadFile = null;
        if (uploadInput) {
          uploadInput.value = '';
        }
        this.message.set('Upload complete.');
      },
      error: (err) => console.error('Failed to upload artifact:', err)
    });
  }

  triggerReplacePicker(): void {
    const input = document.querySelector<HTMLInputElement>('input[type="file"].sr-only');
    input?.click();
  }

  onReplaceFileSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0] ?? null;
    const selected = this.selectedArtifact();
    if (!file || !selected) return;
    if (!this.validateFile(file)) return;

    this.gamePreferencesService.replaceArtifact(selected.id, file).subscribe({
      next: (updated) => {
        this.artifacts.update(list => list.map(a => a.id === updated.id ? updated : a));
        this.selectedArtifact.set(updated);
        this.clearPreviewFor(updated.id);
      },
      error: (err) => console.error('Failed to replace artifact:', err)
    });
  }

  deleteSelectedArtifact(): void {
    const selected = this.selectedArtifact();
    if (!selected) return;

    this.gamePreferencesService.deleteArtifact(selected.id).subscribe({
      next: () => {
        this.clearPreviewFor(selected.id);
        this.artifacts.update(list => list.filter(a => a.id !== selected.id));
        this.selectedArtifact.set(null);
      },
      error: (err) => console.error('Failed to delete artifact:', err)
    });
  }

  isArtifactPlaying(id: number): boolean {
    return this.activePreviewAssetId() === id;
  }

  isConversionPending(artifact: ArtifactAssetDto): boolean {
    return artifact.artifactType.toLowerCase() === 'video'
      && GameEventsPageComponent.PendingStatuses.has((artifact.status ?? '').toLowerCase());
  }

  conversionStatusLabel(artifact: ArtifactAssetDto): string {
    if (!this.isConversionPending(artifact)) {
      return 'Ready';
    }

    return artifact.status === 'ProcessingConversion' ? 'Converting…' : 'Queued';
  }

  conversionInProgressCount(): number {
    return this.visibleArtifacts().filter(a => this.isConversionPending(a)).length;
  }

  previewUrl(id: number): string {
    return this.previewUrlMap.get(id) ?? '';
  }

  async toggleArtifactPreview(artifact: ArtifactAssetDto, event: Event): Promise<void> {
    event.stopPropagation();

    if (this.activePreviewAssetId() === artifact.id) {
      this.activePreviewAssetId.set(null);
      this.previewErrorMessage.set('');
      return;
    }

    this.previewErrorMessage.set('');

    let url = this.previewUrlMap.get(artifact.id);
    if (!url) {
      try {
        const blob = await firstValueFrom(this.gamePreferencesService.getArtifactContent(artifact.id));
        const mediaType = this.resolveMediaType(blob, artifact);
        if (artifact.artifactType.toLowerCase() === 'video' && !this.canAttemptVideoPlayback(mediaType, artifact.fileName)) {
          this.activePreviewAssetId.set(artifact.id);
          this.previewErrorMessage.set(`This browser cannot decode '${artifact.fileName}' (${mediaType || 'unknown codec'}). Try MP4 (H.264/AAC).`);
          return;
        }

        url = this.createPlayableBlobUrl(blob, mediaType);
        this.previewUrlMap.set(artifact.id, url);
      } catch {
        this.activePreviewAssetId.set(artifact.id);
        this.previewErrorMessage.set('Unable to load media preview.');
        return;
      }
    }

    this.activePreviewAssetId.set(artifact.id);
  }

  clearMediaDecodeError(): void {
    if (this.previewErrorMessage()) {
      this.previewErrorMessage.set('');
    }
  }

  onMediaDecodeError(event: Event, artifact: ArtifactAssetDto): void {
    const media = event.target as HTMLMediaElement | null;
    const code = media?.error?.code;

    let detail = 'Unsupported format or codec.';
    if (code === MediaError.MEDIA_ERR_DECODE) {
      detail = 'Decode failed. The browser cannot decode this file.';
    } else if (code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED) {
      detail = 'Source format is not supported by this browser.';
    }

    this.previewErrorMessage.set(`Unable to play '${artifact.fileName}'. ${detail}`);
  }

  formatSize(sizeBytes: number): string {
    if (!sizeBytes) return '0 B';
    if (sizeBytes >= 1024 * 1024) return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
    if (sizeBytes >= 1024) return `${Math.round(sizeBytes / 1024)} KB`;
    return `${sizeBytes} B`;
  }

  private clearPreviewFor(id: number): void {
    const existing = this.previewUrlMap.get(id);
    if (existing) {
      URL.revokeObjectURL(existing);
      this.previewUrlMap.delete(id);
    }

    if (this.activePreviewAssetId() === id) {
      this.activePreviewAssetId.set(null);
      this.previewErrorMessage.set('');
    }
  }

  private createPlayableBlobUrl(blob: Blob, mediaType: string): string {
    if (!mediaType) {
      return URL.createObjectURL(blob);
    }

    const typedBlob = new Blob([blob], { type: mediaType });
    return URL.createObjectURL(typedBlob);
  }

  private resolveMediaType(blob: Blob, artifact: ArtifactAssetDto): string {
    const fallbackType = artifact.artifactType === 'Sound'
      ? this.resolveAudioContentType(artifact.fileName)
      : this.resolveVideoContentType(artifact.fileName);
    const normalizedBlobType = (blob.type ?? '').trim().toLowerCase();
    const normalizedArtifactType = (artifact.contentType ?? '').trim().toLowerCase();
    const isVideo = artifact.artifactType.toLowerCase() === 'video';

    if (normalizedBlobType && normalizedBlobType !== 'application/octet-stream') {
      return isVideo
        ? this.normalizeVideoMediaType(normalizedBlobType, artifact.fileName)
        : this.normalizeAudioMediaType(normalizedBlobType, artifact.fileName);
    }

    const resolved = normalizedArtifactType || fallbackType;
    return isVideo
      ? this.normalizeVideoMediaType(resolved, artifact.fileName)
      : this.normalizeAudioMediaType(resolved, artifact.fileName);
  }

  private resolveAudioContentType(fileName: string): string {
    const lower = (fileName ?? '').toLowerCase();
    if (lower.endsWith('.m4a')) {
      return 'audio/mp4';
    }
    if (lower.endsWith('.wav')) {
      return 'audio/wav';
    }
    if (lower.endsWith('.ogg')) {
      return 'audio/ogg';
    }
    return 'audio/mpeg';
  }

  private normalizeAudioMediaType(mediaType: string, fileName: string): string {
    const type = (mediaType ?? '').toLowerCase().trim();
    const lowerFile = (fileName ?? '').toLowerCase();

    if (!type || type === 'application/octet-stream') {
      return this.resolveAudioContentType(fileName);
    }

    if (lowerFile.endsWith('.m4a')
      || type === 'audio/x-m4a'
      || type === 'audio/m4a'
      || type === 'audio/mp4') {
      return 'audio/mp4';
    }

    return type;
  }

  private canAttemptVideoPlayback(mediaType: string, fileName: string): boolean {
    const lowerName = (fileName ?? '').toLowerCase();
    if (lowerName.endsWith('.wmv')) {
      return true;
    }

    return this.canBrowserPlayVideoType(mediaType);
  }

  private canBrowserPlayVideoType(mediaType: string): boolean {
    if (!mediaType) {
      return false;
    }

    const video = document.createElement('video');
    return video.canPlayType(mediaType) !== '';
  }

  private resolveVideoContentType(fileName: string): string {
    const lower = (fileName ?? '').toLowerCase();
    if (lower.endsWith('.wmv')) {
      return 'video/x-ms-wmv';
    }
    if (lower.endsWith('.mov')) {
      return 'video/quicktime';
    }
    return 'video/mp4';
  }

  private normalizeVideoMediaType(mediaType: string, fileName: string): string {
    const type = (mediaType ?? '').toLowerCase().trim();
    const lowerFile = (fileName ?? '').toLowerCase();

    if (!type || type === 'application/octet-stream') {
      return this.resolveVideoContentType(fileName);
    }

    if (lowerFile.endsWith('.wmv') || type === 'application/x-ms-wmv' || type === 'video/wmv') {
      return 'video/x-ms-wmv';
    }

    if (lowerFile.endsWith('.mov') || type === 'video/mov' || type.includes('quicktime')) {
      return 'video/quicktime';
    }

    if (lowerFile.endsWith('.mp4') || type.includes('mp4')) {
      return 'video/mp4';
    }

    return type;
  }

  private loadArtifacts(): void {
    if (!this.currentOrganizationId || this.currentOrganizationId <= 0) {
      this.artifacts.set([]);
      return;
    }

    this.gamePreferencesService.getArtifactsByOrganization(this.currentOrganizationId).subscribe({
      next: (artifacts) => {
        const baseArtifacts = artifacts.filter(a => a.artifactScope?.toLowerCase() === 'base');
        this.artifacts.set(baseArtifacts);

        if (baseArtifacts.some(a => this.isConversionPending(a))) {
          this.startConversionPolling();
        } else {
          this.stopConversionPolling();
        }
      },
      error: (err) => console.error('Failed to load artifacts:', err)
    });
  }

  private startConversionPolling(): void {
    if (this.conversionPollingHandle) {
      return;
    }

    this.conversionPollingHandle = setInterval(() => {
      this.loadArtifacts();
    }, 3000);
  }

  private stopConversionPolling(): void {
    if (!this.conversionPollingHandle) {
      return;
    }

    clearInterval(this.conversionPollingHandle);
    this.conversionPollingHandle = null;
  }

  private validateFile(file: File): boolean {
    const extension = file.name.includes('.') ? file.name.substring(file.name.lastIndexOf('.')).toLowerCase() : '';

    if (this.activeSection() === 'sounds') {
      if (!this.allowedSoundExtensions.has(extension)) {
        window.alert('Unsupported sound format. Allowed: m4a, wav.');
        return false;
      }
      if (file.size > this.maxSoundBytes) {
        window.alert('Sound file exceeds 5 MB limit.');
        return false;
      }
      return true;
    }

    if (this.activeSection() === 'video') {
      if (!this.allowedVideoExtensions.has(extension)) {
        window.alert('Unsupported video format. Allowed: mp4, wmv, mov, webm, webp.');
        return false;
      }
      if (file.size > this.maxVideoBytes) {
        window.alert('Video file exceeds 5 MB limit.');
        return false;
      }
    }

    return true;
  }
}
